import { SlideWaveBackground } from "@/components/investors/slides/SlideWaveBackground";

export function LegalCTASlide({ isActive: _ }: { isActive: boolean }) {
  return (
    <div className="relative flex flex-col justify-center h-full">
      <SlideWaveBackground />
      <div className="relative z-10 flex gap-12">
        {/* Left — CTA content */}
        <div className="flex-1">
          <h2 className="text-[32px] font-light tracking-tight leading-snug font-[family-name:var(--font-heading)] mb-4 max-w-[520px]">
            See how AI paralegal agents can support your practice.
          </h2>
          <p className="text-[14px] text-[var(--color-fg-secondary)] leading-relaxed font-light max-w-[440px] mb-8">
            We&apos;ll walk through your current workflows and show how an AI paralegal agent can support your team inside the tools they already use, with no disruption to your existing systems.
          </p>

          {/* What happens next */}
          <div>
            <p className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mb-3">
              What happens next
            </p>
            <ol className="space-y-2">
              {[
                "A 30-minute call to understand your current workflow",
                "We identify where the agent can reduce manual preparation",
                "A tailored demo using your practice area as the example",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="text-[11px] font-[family-name:var(--font-mono)] text-[var(--color-accent)] font-medium mt-px">{i + 1}.</span>
                  <span className="text-[13px] text-[var(--color-fg-secondary)] leading-snug">{item}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Right — Contact + integrations */}
        <div className="w-[30%] shrink-0 flex flex-col justify-center">
          <div className="border-l border-[var(--color-border)] pl-8">
            <p className="text-[16px] font-medium font-[family-name:var(--font-heading)] mb-1">Ben Zhang</p>
            <a href="mailto:ben@cosx.co" className="text-[13px] font-[family-name:var(--font-mono)] text-[var(--color-accent)] hover:opacity-80 transition-opacity block mb-1">ben@cosx.co</a>
            <a href="https://cosx.co" target="_blank" rel="noopener noreferrer" className="text-[13px] font-[family-name:var(--font-mono)] text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors block mb-3">cosx.co</a>
            <p className="text-[9px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)]">
              London · Sydney · Singapore
            </p>
          </div>

          {/* Integration logos */}
          <div className="border-l border-[var(--color-border)] pl-8 mt-8">
            <p className="text-[9px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mb-3">
              Works where you work
            </p>
            <div className="flex items-center gap-4">
              {[
                { name: "Slack", icon: "M14.5 10c-.83 0-1.5-.67-1.5-1.5V3c0-.83.67-1.5 1.5-1.5S16 2.17 16 3v5.5c0 .83-.67 1.5-1.5 1.5zM20 10h-1.5c-.83 0-1.5-.67-1.5-1.5S17.67 7 18.5 7H20c.83 0 1.5.67 1.5 1.5S20.83 10 20 10zM9.5 14c.83 0 1.5.67 1.5 1.5V21c0 .83-.67 1.5-1.5 1.5S8 21.83 8 21v-5.5c0-.83.67-1.5 1.5-1.5zM4 14h1.5c.83 0 1.5.67 1.5 1.5S6.33 17 5.5 17H4c-.83 0-1.5-.67-1.5-1.5S3.17 14 4 14zM10 9.5c0 .83-.67 1.5-1.5 1.5H3c-.83 0-1.5-.67-1.5-1.5S2.17 8 3 8h5.5c.83 0 1.5.67 1.5 1.5zM10 4v1.5c0 .83-.67 1.5-1.5 1.5S7 6.33 7 5.5V4c0-.83.67-1.5 1.5-1.5S10 3.17 10 4zM14 14.5c0-.83.67-1.5 1.5-1.5H21c.83 0 1.5.67 1.5 1.5S21.83 16 21 16h-5.5c-.83 0-1.5-.67-1.5-1.5zM14 20v-1.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V20c0 .83-.67 1.5-1.5 1.5S14 20.83 14 20z" },
                { name: "Teams", icon: "M16.5 12.5c1.38 0 2.5-1.12 2.5-2.5S17.88 7.5 16.5 7.5 14 8.62 14 10s1.12 2.5 2.5 2.5zM9 11c1.66 0 3-1.34 3-3S10.66 5 9 5 6 6.34 6 8s1.34 3 3 3zm7.5 3h-1c-.28 0-.5.22-.5.5v5c0 .28.22.5.5.5H19c1.1 0 2-.9 2-2v-2c0-1.1-.9-2-2-2h-2.5zM9 13c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z" },
                { name: "WhatsApp", icon: "M12 2C6.48 2 2 6.48 2 12c0 1.77.46 3.43 1.27 4.88L2 22l5.23-1.23C8.69 21.54 10.31 22 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm.02 18c-1.53 0-2.96-.41-4.2-1.12l-.3-.18-3.12.82.83-3.04-.2-.31A7.93 7.93 0 014.02 12c0-4.41 3.58-8 7.98-8s8.02 3.59 8.02 8-3.58 8-8 8z" },
                { name: "Email", icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" },
              ].map((app) => (
                <div key={app.name} className="flex flex-col items-center gap-1">
                  <svg className="w-5 h-5 text-[var(--color-fg-tertiary)]" fill={app.name === "Email" ? "none" : "currentColor"} stroke={app.name === "Email" ? "currentColor" : "none"} strokeWidth={app.name === "Email" ? 1.5 : 0} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d={app.icon} />
                  </svg>
                  <span className="text-[7px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] tracking-wider">{app.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
